import {
  Autocomplete,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import _ from "lodash";
import {
  FC,
  startTransition,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  useDropdownSourceContext,
  useDynamicFormsTranslate,
} from "@ryde/redux";
import {
  IDropdownItem,
  IDropdownItemExt,
  IFormlessInput,
  Log,
  sortArrayAlphabetically,
  ValidationHelpers,
} from "@ryde/shared";

import {
  dropDownBoundTemplateFormat,
  getDropdownOptions,
} from "../../../dynamic-forms";

interface IMultiSelectBoundFormlessProps extends IFormlessInput {
  value: number[];
  onChange: (value: number[]) => void;
  error?: { message: string };
  labelTranslated?: string;
}

// name will be relative to the control of form passed to the input
// can be used to create independent nested forms
export const MultiSelectBoundFormless: FC<IMultiSelectBoundFormlessProps> = ({
  name,
  formLabel,
  formValidation,
  formAttributes,
  className,
  value,
  onChange,
  error,
  labelTranslated,
}) => {
  const { t } = useTranslation();
  const { getValues } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    fallbackText: formLabel,
    translatedText: labelTranslated,
  });
  const { getDropdownItems } = useDropdownSourceContext();
  const [dropdownItems, setDropdownItems] = useState<
    IDropdownItem[] | undefined
  >(undefined);
  const initial: number[] = [];

  const {
    dropItemsSource,
    dropdownItemsFilter,
    excludeIds,
    disableSorting,
    customDropdownItemsFilter,
    dropDownTemplate,
  } = formAttributes || {};

  const currentFieldValue = getValues(name);

  const { ownFieldName, filters } = dropdownItemsFilter || {};
  const [dropdownOptions, mapOptionLabel] = useMemo(
    () =>
      getDropdownOptions({
        ownFieldName,
        dropdownItems: (!disableSorting
          ? sortArrayAlphabetically<IDropdownItem>(dropdownItems, "label")
          : dropdownItems) as IDropdownItemExt[],
        sourceFieldValue: undefined,
        filters,
        excludeIds,
        currentFieldValue,
        customDropdownItemsFilter,
        transformLabelFn: dropDownTemplate
          ? itemData =>
              dropDownBoundTemplateFormat({
                itemData,
                dropDownTemplate,
              })
          : undefined,
      }),
    [
      ownFieldName,
      disableSorting,
      dropdownItems,
      filters,
      excludeIds,
      currentFieldValue,
      customDropdownItemsFilter,
      dropDownTemplate,
    ],
  );

  const getOptionLabel = useCallback(
    (val: unknown) => {
      return mapOptionLabel.get(val as string | number) || "";
    },
    [mapOptionLabel],
  );

  if (initial && !_.isArray(initial)) {
    Log.error(t("errors.multiSelectBoundTemplateInitialValueNotArray"));
  }

  const [innerValue, setInnerValue] = useState<number[]>(initial || []);

  useEffect(() => {
    const getItems = async (): Promise<void> => {
      const items = await getDropdownItems?.(dropItemsSource || "unknown");
      setDropdownItems(items || []);
    };

    if (dropItemsSource) {
      startTransition(() => {
        getItems();
      });
    }
  }, [getDropdownItems, dropItemsSource]);
  const autocompleteValue = useMemo(() => {
    const currentSelection = innerValue.length > 0 ? innerValue : value || [];
    if (disableSorting) {
      return currentSelection;
    }
    return currentSelection.toSorted((a, b) => {
      const labelA = getOptionLabel(a).toLowerCase();
      const labelB = getOptionLabel(b).toLowerCase();
      return labelA.localeCompare(labelB);
    });
  }, [innerValue, value, getOptionLabel, disableSorting]);

  return (
    <FormControl fullWidth error={!!error} className={className}>
      <Autocomplete
        multiple
        data-e2e-id={`multi_select_${name}`}
        options={dropdownOptions || []}
        value={autocompleteValue}
        onChange={(_event, newValue) => {
          if (!newValue) {
            return;
          }

          setInnerValue(newValue as number[]);
          onChange(newValue as number[]);
        }}
        getOptionLabel={getOptionLabel}
        disableCloseOnSelect
        renderInput={params => (
          <TextField
            {...params}
            error={!!error}
            label={formLabelTranslated}
            required={ValidationHelpers.hasRequiredRule(formValidation)}
            fullWidth
          />
        )}
      />
      {error && <FormHelperText>{error?.message}</FormHelperText>}
    </FormControl>
  );
};
